import { Building2, Mail, Globe, MapPin, FileText } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/bits";
import { IMAGES } from "@/data/site";
import { CONTACT_EMAIL } from "@/lib/api";
import { Link } from "react-router-dom";

export default function Sobre() {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Sobre nós"
        title="OFF360 — Conectando você ao que importa."
        lead="O OFF360 nasceu com o propósito de aproximar pessoas, serviços, negócios e oportunidades por meio da tecnologia."
      />

      <section className="mx-auto max-w-8xl px-4 py-16 sm:px-6 md:py-20 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <SectionHead eyebrow="Nosso propósito" title="Tecnologia que simplifica o cotidiano." />
            <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              <p>
                Nossa atuação começa pela mobilidade urbana, com o lançamento do 360Taxi, e foi planejada para evoluir
                gradualmente para novas soluções destinadas a consumidores, empresários e prestadores de serviços.
              </p>
              <p>
                Acreditamos que a tecnologia deve simplificar o cotidiano, facilitar conexões e ampliar
                possibilidades.
              </p>
              <p className="font-semibold text-slate-900">Nossa base de lançamento está em Campinas, São Paulo.</p>
            </div>
            <BtnLikeLink />
          </Reveal>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-2xl">
              <img
                src={IMAGES.skyline}
                alt="Vista noturna de centro urbano brasileiro"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-16 md:py-20" data-testid="sobre-info-empresariais">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHead eyebrow="Informações empresariais" title="Dados oficiais da OFF360." />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {[
                { icon: Building2, t: "Razão social", d: "OFF360 TECNOLOGIA SERVICOS E SOLUCOES LTDA" },
                { icon: FileText, t: "CNPJ", d: "69.590.691/0001-27" },
                { icon: Mail, t: "Contato", d: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
                { icon: Globe, t: "Site", d: "www.off360.com.br" },
                { icon: MapPin, t: "Cidade", d: "Campinas — São Paulo" },
              ].map((f) => (
                <div key={f.t} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
                    <f.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      {f.t}
                    </p>
                    {f.href ? (
                      <a href={f.href} className="mt-1 block text-sm font-bold text-slate-900 hover:text-brand-orange sm:text-base">
                        {f.d}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm font-bold text-slate-900 sm:text-base">{f.d}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm leading-relaxed text-slate-500">
              O endereço oficial, telefone e demais informações cadastrais serão conferidos com os dados mais
              recentes fornecidos pela administração antes da publicação definitiva. Nenhum dado fictício é
              publicado neste site.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function BtnLikeLink() {
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <Link
        to="/360taxi"
        data-testid="sobre-link-360taxi"
        className="rounded-full bg-brand-orange px-7 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-orangedark"
      >
        Conheça o 360Taxi
      </Link>
      <Link
        to="/futuro-off360"
        data-testid="sobre-link-futuro"
        className="rounded-full border border-slate-300 px-7 py-3.5 text-sm font-bold text-slate-800 transition-all hover:-translate-y-0.5 hover:border-brand-orange hover:text-brand-orange"
      >
        Futuro OFF360
      </Link>
    </div>
  );
}
